import { forwardRef, useRef, useCallback, useImperativeHandle } from 'react';
import { MessageBubble } from './MessageBubble';
import type { ChatMessage } from '../../services/chatTypes';
import './MessageList.css';

interface MessageListProps {
  messages: ChatMessage[];
}

export const MessageList = forwardRef<HTMLDivElement, MessageListProps>(
  function MessageList({ messages }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const messageRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Expose the container ref to the parent (for auto-scroll)
    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    const setMessageRef = useCallback(
      (index: number) => (el: HTMLDivElement | null) => {
        messageRefs.current[index] = el;
      },
      []
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (messages.length === 0) return;

        const target = e.target as HTMLElement;
        const isContainer = target === containerRef.current;
        const isMessageBubble = target.hasAttribute('data-message-index');

        if (!isContainer && !isMessageBubble) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (isContainer) {
            // Focus the first message
            messageRefs.current[0]?.focus();
          } else {
            const currentIndex = Number(target.getAttribute('data-message-index'));
            const nextIndex = currentIndex + 1;
            if (nextIndex < messages.length) {
              messageRefs.current[nextIndex]?.focus();
            }
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (isContainer) {
            // Focus the last message
            messageRefs.current[messages.length - 1]?.focus();
          } else {
            const currentIndex = Number(target.getAttribute('data-message-index'));
            const prevIndex = currentIndex - 1;
            if (prevIndex >= 0) {
              messageRefs.current[prevIndex]?.focus();
            } else {
              // Move focus back to container
              containerRef.current?.focus();
            }
          }
        }
      },
      [messages.length]
    );

    return (
      <div
        ref={containerRef}
        className="message-list"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Chat messages"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {messages.map((message, index) => (
          <MessageBubble
            key={message.id}
            message={message}
            ref={setMessageRef(index)}
            index={index}
          />
        ))}
      </div>
    );
  }
);
